/**
 * R0006 Access Control Configuration
 * Defines role-based access to areas and stores
 */

export const R0006_ACCESS_CONTROL = {
  // Roles with full access to all areas and stores
  FULL_ACCESS_ROLES: ['Admin', 'Exec', 'AreaMgr'],
  
  // Role-specific access rules
  ROLE_ACCESS: {
    'GenMgr': {
      type: 'AREA_RESTRICTED',
      // General Managers can only see their own area
      canAccessOwnAreaOnly: true
    }
  }
};

/**
 * Determines if a user has full access to R0006
 * @param {string} userRole - The user's role
 * @returns {boolean}
 */
export const hasFullAccess = (userRole) => {
  return R0006_ACCESS_CONTROL.FULL_ACCESS_ROLES.includes(userRole);
};

/**
 * Gets access configuration for a specific role
 * @param {string} userRole - The user's role
 * @returns {object} Access configuration
 */
export const getRoleAccess = (userRole) => {
  if (hasFullAccess(userRole)) {
    return { type: 'FULL_ACCESS' };
  }
  
  return R0006_ACCESS_CONTROL.ROLE_ACCESS[userRole] || { type: 'NO_ACCESS' };
};

/**
 * Filters areas based on user's role and assigned area(s)/store(s)
 * @param {Array} areas - All available areas
 * @param {object} user - User object with role and area/store information
 * @returns {Array} Filtered areas the user can access
 */
export const filterAreasByAccess = (areas, user, stores) => {
  if (!user || !areas) return [];
  
  const roleAccess = getRoleAccess(user.role);
  
  switch (roleAccess.type) {
    case 'FULL_ACCESS':
      return areas;
      
    case 'AREA_RESTRICTED':
      // Handle multiple scenarios for GenMgr access
      if (roleAccess.canAccessOwnAreaOnly) {
        // Special handling for GenMgr - derive areas from their stores
        if (user.role === 'GenMgr' && stores) {
          const userName = user.userName || user.displayName;
          // First, find stores where this user is a GM
          const userStores = stores.filter(store => 
            store.gm && Array.isArray(store.gm) && store.gm.includes(userName)
          );
          
          // Then find areas that contain these stores
          const userStoreNames = userStores.map(s => s.shortName || s.name);
          console.log('🔍 GenMgr stores:', userStoreNames);
          
          const accessibleAreas = areas.filter(area => {
            if (!area.stores || !Array.isArray(area.stores)) return false;
            
            // Check if any of the user's stores are in this area
            const hasUserStore = area.stores.some(areaStore => {
              const areaStoreName = typeof areaStore === 'object' ? areaStore.shortName : areaStore;
              return userStoreNames.includes(areaStoreName);
            });
            
            if (hasUserStore) {
              console.log(`✅ GenMgr has access to area: ${area.areaName || area.name}`);
            }
            return hasUserStore;
          });
          
          return accessibleAreas;
        }
        // Scenario 1: User has area(s) assigned directly
        if (user.area) {
          return areas.filter(area => area.id === user.area || area.name === user.area);
        }
        
        // Scenario 2: User has multiple areas assigned (if stored as array)
        if (user.areas && Array.isArray(user.areas)) {
          return areas.filter(area => 
            user.areas.some(userArea => 
              area.id === userArea || area.name === userArea
            )
          );
        }
        
        // Scenario 3: User has stores assigned - derive areas from stores
        if (user.stores && Array.isArray(user.stores)) {
          // Find all areas that contain any of the user's stores
          const userStoreNames = user.stores.map(s => typeof s === 'string' ? s : s.name || s.shortName);
          return areas.filter(area => 
            area.stores && area.stores.some(store => 
              userStoreNames.includes(store) ||
              userStoreNames.includes(store.name) ||
              userStoreNames.includes(store.shortName)
            )
          );
        }
        
        // Scenario 4: User has a single store assigned
        if (user.store) {
          const userStoreName = typeof user.store === 'string' ? user.store : user.store.name || user.store.shortName;
          return areas.filter(area => 
            area.stores && area.stores.some(store => 
              store === userStoreName ||
              store.name === userStoreName ||
              store.shortName === userStoreName
            )
          );
        }
      }
      return [];
      
    case 'NO_ACCESS':
    default:
      return [];
  }
};

/**
 * Filters stores based on user's role and assigned area(s)/store(s)
 * @param {Array} stores - All available stores
 * @param {object} user - User object with role and area/store information
 * @param {Array} areas - All areas (to find stores within user's area)
 * @returns {Array} Filtered stores the user can access
 */
export const filterStoresByAccess = (stores, user, areas) => {
  if (!user || !stores) return [];
  
  console.log('🔍 filterStoresByAccess - User data:', {
    role: user.role,
    userName: user.userName,
    displayName: user.displayName,
    stores: user.stores,
    store: user.store,
    area: user.area,
    areas: user.areas,
    storeCount: stores?.length
  });
  
  const roleAccess = getRoleAccess(user.role);
  
  switch (roleAccess.type) {
    case 'FULL_ACCESS':
      return stores;
      
    case 'AREA_RESTRICTED':
      if (roleAccess.canAccessOwnAreaOnly) {
        // For GenMgr role, return all stores in areas where the user is a GM
        if (user.role === 'GenMgr') {
          const userName = user.userName || user.displayName;
          console.log('🔍 Filtering stores for GenMgr (all stores in accessible areas):', userName);
          
          // Get the accessible areas for this GenMgr (reuse the area filtering logic)
          const accessibleAreas = filterAreasByAccess(areas, user, stores);
          console.log('🏢 GenMgr accessible areas:', accessibleAreas.map(a => a.areaName || a.name));
          
          if (accessibleAreas.length === 0) {
            console.log('❌ No accessible areas found for GenMgr');
            return [];
          }
          
          // Get all store names from accessible areas
          const accessibleStoreNames = new Set();
          accessibleAreas.forEach(area => {
            if (area.stores && Array.isArray(area.stores)) {
              area.stores.forEach(store => {
                const storeName = typeof store === 'object' ? 
                  (store.shortName || store.name) : store;
                accessibleStoreNames.add(storeName);
              });
            }
          });
          
          console.log('🏪 All accessible store names:', Array.from(accessibleStoreNames));
          
          // Filter all stores to include only those in accessible areas
          const accessibleStores = stores.filter(store => {
            const storeName = store.shortName || store.name;
            const isAccessible = accessibleStoreNames.has(storeName);
            if (isAccessible) {
              console.log(`✅ GenMgr can access store: ${storeName}`);
            }
            return isAccessible;
          });
          
          console.log(`📊 Found ${accessibleStores.length} accessible stores for GenMgr ${userName}`);
          return accessibleStores;
        }
        
        // Original logic for other scenarios
        if (user.stores && Array.isArray(user.stores)) {
          console.log('📍 Using user.stores array:', user.stores);
          const userStoreNames = user.stores.map(s => typeof s === 'string' ? s : s.name || s.shortName);
          console.log('📍 Mapped store names:', userStoreNames);
          return stores.filter(store => 
            userStoreNames.includes(store.id) ||
            userStoreNames.includes(store.name) ||
            userStoreNames.includes(store.shortName)
          );
        }
        
        // Scenario 2: User has single store assigned
        if (user.store) {
          const userStoreName = typeof user.store === 'string' ? user.store : user.store.name || user.store.shortName;
          return stores.filter(store => 
            store.id === userStoreName ||
            store.name === userStoreName ||
            store.shortName === userStoreName
          );
        }
        
        // Scenario 3: User has area(s) assigned - get all stores in those areas
        const accessibleAreas = filterAreasByAccess(areas, user);
        if (accessibleAreas.length > 0) {
          const storeNamesInAreas = new Set();
          accessibleAreas.forEach(area => {
            if (area.stores) {
              area.stores.forEach(store => {
                if (typeof store === 'string') {
                  storeNamesInAreas.add(store);
                } else if (store) {
                  storeNamesInAreas.add(store.name || store.shortName || store.id);
                }
              });
            }
          });
          
          return stores.filter(store => 
            storeNamesInAreas.has(store.id) ||
            storeNamesInAreas.has(store.name) ||
            storeNamesInAreas.has(store.shortName)
          );
        }
      }
      return [];
      
    case 'NO_ACCESS':
    default:
      return [];
  }
};

/**
 * Checks if user has access to R0006 report
 * @param {object} user - User object
 * @returns {boolean}
 */
export const canAccessR0006 = (user) => {
  if (!user || !user.role) return false;
  
  const roleAccess = getRoleAccess(user.role);
  return roleAccess.type !== 'NO_ACCESS';
};

/**
 * Gets a descriptive message about user's access level
 * @param {object} user - User object
 * @param {Array} accessibleAreas - Areas the user can access (optional)
 * @returns {string}
 */
export const getAccessLevelMessage = (user, accessibleAreas) => {
  if (!user || !user.role) return 'No access';
  
  const roleAccess = getRoleAccess(user.role);
  
  switch (roleAccess.type) {
    case 'FULL_ACCESS':
      return 'Full access to all areas and stores';
    case 'AREA_RESTRICTED':
      // Build a more accurate message based on what the user has access to
      if (user.stores && Array.isArray(user.stores)) {
        const storeCount = user.stores.length;
        if (storeCount === 1) {
          return `Access limited to ${user.stores[0]} store only`;
        } else {
          return `Access limited to ${storeCount} assigned stores`;
        }
      } else if (user.store) {
        return `Access limited to ${user.store} store only`;
      } else if (user.areas && Array.isArray(user.areas)) {
        const areaCount = user.areas.length;
        if (areaCount === 1) {
          return `Access limited to ${user.areas[0]} area only`;
        } else {
          return `Access limited to ${areaCount} assigned areas`;
        }
      } else if (user.area) {
        return `Access limited to ${user.area} area only`;
      } else if (accessibleAreas && accessibleAreas.length > 0) {
        if (accessibleAreas.length === 1) {
          return `Access limited to ${accessibleAreas[0].name || accessibleAreas[0].areaName} area only`;
        } else {
          return `Access limited to ${accessibleAreas.length} areas`;
        }
      }
      return 'Access limited to assigned locations';
    case 'NO_ACCESS':
    default:
      return 'No access to this report';
  }
};